import { type FC, useState } from "react"
import styled, { css } from "styled-components"

import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { type Course, useCourses } from "~/api/course"
import Button from "~/themes/school/components/Button"
import Card, {
	CardHeader,
	CardSticker,
	CardText,
	CardThumbnail,
	CardTitle,
	Cards,
} from "~/themes/school/components/Card"
import { TitleSearch } from "~/themes/school/components/ContentSearch"
import { CheckIcon } from "~/themes/school/components/Icon"
import Layout from "~/themes/school/components/Layout"
import PageHeader, { PageTitle } from "~/themes/school/components/PageHeader"
import PartialLoadingPage from "~/themes/school/components/PartialLoadingPage"
import { fonts, successColor } from "~/themes/school/styles"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"

const CoursesPage: FC = () => {
	const courses = useCourses()
	const t = useTranslation()

	const [selectedTag, setSelectedTag] = useState<number>()
	const [searchItems, setSearchItems] = useState<number[]>()

	return (
		<Layout>
			<PageHeader>
				<PageTitle>{t("course.title")}</PageTitle>
				<TitleSearch
					items={courses.status === "success" ? courses.data : []}
					onItemsToShowChange={setSearchItems}
				/>
			</PageHeader>
			{courses.status === "error" ? (
				<p>{t("failedToLoad")}</p>
			) : courses.status === "loading" ? (
				<PartialLoadingPage />
			) : courses.status === "success" ? (
				<>
					{courses.tags.length > 0 && (
						<Tags>
							{courses.tags.map(tag => (
								<Tag
									key={tag.id}
									$isActive={selectedTag === tag.id}
									onClick={() => setSelectedTag(current => (current !== tag.id ? tag.id : undefined))}
								>
									{tag.label}
								</Tag>
							))}
							{selectedTag !== undefined && (
								<Tag $isActive onClick={() => setSelectedTag(undefined)}>
									{t("clearFilter")}
								</Tag>
							)}
						</Tags>
					)}
					<Courses courses={courses.data} selectedTag={selectedTag} searchItems={searchItems} />
				</>
			) : (
				exhaustiveGuard(courses.status)
			)}
		</Layout>
	)
}

type CoursesProps = { courses: Course[]; selectedTag: number | undefined; searchItems: number[] | undefined }
const Courses: FC<CoursesProps> = ({ courses, selectedTag, searchItems }) => {
	let filteredCourses = courses
	if (selectedTag !== undefined) {
		filteredCourses = filteredCourses.filter(course => course.tagIds.includes(selectedTag))
	}
	if (searchItems !== undefined) {
		filteredCourses = filteredCourses
			.filter(course => searchItems.includes(course.id))
			.sort((a, b) => (searchItems ? searchItems.indexOf(b.id) - searchItems.indexOf(a.id) : 0))
	}
	return (
		<Cards>
			{filteredCourses.map(course => (
				<Card key={course.id} as={Button} onClick={routes.course.detail(course.id)}>
					<CardHeader>
						<CourseTitle $isCompleted={course.progress === 1}>
							{course.title}
							{course.progress === 1 && <CourseTitleCheckIcon />}
						</CourseTitle>
						<CardSticker>{course.pagesLabel}</CardSticker>
					</CardHeader>
					<CardThumbnail
						filePath={course.thumbnailFilePath}
						sticker={<Price>{course.priceLabel.value}</Price>}
					/>
					<CardText>{course.shortDescription}</CardText>
				</Card>
			))}
		</Cards>
	)
}

const Tags = styled.div`
	width: 100%;
	display: flex;
	gap: 22px;
	flex-wrap: wrap;
	margin-bottom: 24px;
`

const Tag = styled(Button)<{ $isActive: boolean }>`
	font-family: ${fonts.quicksand};
	font-size: 14px;
	font-weight: bold;
	color: ${props => (props.$isActive ? props.theme.primaryColor : "#454D66")};
`

const CourseTitle = styled(CardTitle)<{ $isCompleted: boolean }>`
	${props =>
		props.$isCompleted &&
		css`
			color: ${successColor};
			display: flex;
			align-items: center;
			gap: 8px;
		`}
`

const CourseTitleCheckIcon = styled(CheckIcon)`
	width: 12px;
	height: 12px;
`

const Price = styled.p`
	position: absolute;
	top: 16px;
	right: 16px;
	font-weight: bold;
	font-size: 16px;
	color: #ffa412;
	background-color: white;
	padding: 4px 8px;
`

export default CoursesPage
